type FeatureFlag = {
  value: string;
  description: string;
};

/**
 * IMPORTANT, When adding a new feature flag, please add team, name, and description.
 *
 * Teams are ENG, SUPPORT, PRODUCT
 */
export const featureFlags: FeatureFlag[] = [
  {
    value: "seo-roi-tracker",
    description:
      "(PRODUCT, Berkley) Enables SEO ROI tracker setup and HTML customer reviews in Insights. Off by default.",
  },
  {
    value: "prevent-theme-header-footer",
    description:
      "(SUPPORT) Prevents theme-defined headers and footers from being automatically applied.",
  },
  {
    value: "archived-pages",
    description:
      "(SUPPORT) Enables support for managing and restoring archived pages.",
  },

  {
    value: "component-previews",
    description:
      "(ENG, Gabe) Renders component JSON outside the canvas instead of screenshots.",
  },
  {
    value: "write-through-products-sync",
    description:
      "(ENG, Noah) When loading products, we request them from the storefront API if possible and write them through to the database.",
  },
  {
    value: "replo-agent-opus",
    description:
      "(ENG, Gabe) Uses Claude Opus 4.5 model for chat agent instead of Sonnet.",
  },
  {
    value: "site-ui-overhaul",
    description:
      "(PRODUCT, Yuxin) Site Builder UI overhaul: browser-style header with a URL bar, site chip and settings menu, full-screen New page, and the reworked Site settings and side panel. Off by default.",
  },
  {
    value: "shared-components",
    description:
      "(ENG, Ezra) Gates the Component Library route and related entry points.",
  },
  {
    value: "agent-onboarding-home",
    description:
      "(PRODUCT, Kevin) Replaces the Launchpad mini-app with the onboarding checklist on agent home. Off means Launchpad, on means home carries setup itself.",
  },
  {
    value: "home-workflow-sessions",
    description:
      "(PRODUCT, Yuxin) Home workflow build and publish steps run as their own sessions: a prompt box, then a status line with the page previewed on desktop and mobile. Off drafts the prompt into the chat and marks the step done by hand.",
  },
  {
    value: "replo-home-recommendations",
    description:
      "(PRODUCT, Kevin) Once setup finishes, hands the agent home checklist over to agent-written goal recommendations for the project.",
  },
  {
    value: "site-reports",
    description:
      "(PRODUCT, Yuxin) Gates the website-builder Site Reports (site audits) UI. Off by default (disabled in production) while evidence collection is broken; Page Audit is unaffected.",
  },
  {
    value: "replo-agent-model",
    description:
      "(ENG, Gabe) Multivariate: the variant name sets the main chat agent's model. Variants match an AgentModelChoice or OpenRouter slug kebab-cased to PostHog's charset (glm-4-6, anthropic-claude-sonnet-5). Explicit client-set model overrides still win; non-Anthropic choices route via OpenRouter automatically. Configure: https://app.posthog.com/project/17701/feature_flags/843182",
  },
  {
    value: "mini-app-database-record-tools",
    description:
      "(PRODUCT, Noah) Exposes the five mini-app database record MCP tools (create/read/update/query/delete) to the agent. Off by default; the six schema-management tools are always registered.",
  },
  {
    value: "messages-integration",
    description:
      "(PRODUCT, Kevin) Enables Messages connection setup, phone verification, and chat access for the user. Off by default.",
  },
  {
    value: "meta-ads-integration",
    description:
      "(ENG/PRODUCT, Kevin) Shows the Meta Ads (Meta Marketing API) integration card in the integrations catalog. Off by default while Meta App Review is pending; enable per-account for testing.",
  },
  {
    value: "buy-domains",
    description:
      "(ENG, Yuxin) Enables buying a domain through Replo: search, registrant contact, and checkout. Gates acquisition only — managing an already-registered domain stays available.",
  },
  {
    value: "sightglass",
    description:
      "(ENG, Yuxin) Shows the Sightglass miniapp (competitor Meta ads and landing pages) in the launcher, home, widget library and tab routing. Team-only until the corpus is seeded.",
  },
  {
    value: "sightglass-offer-calendar",
    description:
      "(ENG, Yuxin) Shows Sightglass offers: the Calendar tab of saved brands' campaigns, a company's Offers section, Arena's Offers ranking and a landing page's Versions strip. Team-only until offer reads are backfilled.",
  },
  {
    value: "agent-import-site-page",
    description:
      "(ENG, Yuxin) Lets the sandbox agent recreate a URL, HTML file or screenshot through the deterministic import-site-page tool instead of authoring every line itself.",
  },
  {
    value: "agent-viewport-turn-screenshots",
    description:
      "(ENG, Yuxin) Captures a viewport DOM snapshot at agent turn end and uploads it for the admin session filmstrip.",
  },
  {
    value: "canvas",
    description:
      "(ENG, Yuxin) Shows the Canvas miniapp (the ad maker: infinite canvas, frames, templates, export) in the launcher and tab routing. Team-only until the editor is complete.",
  },
  {
    value: "artifacts",
    description:
      "(ENG, Noah) Enables artifact authoring and management in Files, the artifact creation skill, and artifact authoring tools. Shared artifact viewing remains available regardless of this flag.",
  },
  {
    value: "canvas-quiz",
    description:
      "(ENG, Yuxin) Shows the Quiz panel in Canvas: quiz concept steps, campaign sections, reference screenshots, and Build, which sends the selection to chat as a prompt. Needs the `canvas` flag too.",
  },
  {
    value: "catalog-products",
    description:
      "(PRODUCT, Yuxin) Enables the 'Choose from catalog' path in the Products miniapp (browse ready-made Printful catalog products, then customize the design). Off shows the disabled 'Coming soon' card.",
  },
  {
    value: "project-context",
    description:
      "(ENG, Yuxin) Branding opens the single project-context editor (draft/publish rows) instead of the brand directory for projects that have no project_context rows yet. Projects with rows always get the editor; reads never consult this flag. Team-only until the migration has run.",
  },
  {
    value: "v1-migration",
    description:
      "(PRODUCT, Patrick) Lets customers bring published Replo v1 pages into their Agent site from a Home card, and lets the agent migrate the rest on request.",
  },
  {
    value: "sandbox-egress-allowlist",
    description:
      "(ENG, Andrew) Creates the actor's new sandboxes with the Daytona egress allowlist. Create-time only: existing boxes keep their network settings.",
  },
];

export type InAppAnnouncementFlag = `${string}-announcement-${string}`;
export type FeatureName =
  | FeatureFlag["value"]
  | `announcement_${string}`
  | InAppAnnouncementFlag;

// NOTE (Cole, 2026-07-20): email is the distinct_id everywhere (a UUID would change rollout buckets); person_properties covers unstored persons.
export function buildPosthogDecideRequestBody({
  token,
  email,
  groups,
}: {
  token: string;
  email: string;
  groups?: { project?: string; workspace?: string };
}) {
  return {
    token,
    distinct_id: email,
    person_properties: { email },
    ...(groups ? { groups } : {}),
  };
}
